import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { WordMark } from "@/components/brand";
import { POOL_NIGHT } from "@/constants/media";
import AuthNav from "@/components/site/AuthNav";
import InstallApp from "@/components/InstallApp";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleOn, setGoogleOn] = useState(false);

  useEffect(() => { api.get("/auth/google/status").then((r) => setGoogleOn(r.data.configured)).catch(() => {}); }, []);

  const googleLogin = async () => {
    try { const { data } = await api.get("/auth/google/url"); window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (!u.is_staff && u.account_status && u.account_status !== "Active") {
        nav("/pending");
        return;
      }
      toast.success("Welcome back to The D.");
      const dest = loc.state?.from || (u.is_staff ? "/admin" : "/portal");
      nav(dest);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <AuthNav />
      <div className="relative hidden lg:block">
        <img src={POOL_NIGHT} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 h-full flex items-end p-12"><p className="font-serif text-3xl italic text-gold text-glow-gold max-w-sm">Arrive as a guest. Leave as part of The D.</p></div>
      </div>
      <div className="flex items-center justify-center p-8 bg-ink">
        <div className="w-full max-w-sm">
          <Link to="/"><WordMark size="text-4xl" /></Link>
          <h1 className="font-serif text-3xl mt-10 mb-1 text-center">Member Login</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">Sign in to your private account.</p>
          <form onSubmit={submit} className="space-y-4">
            <input type="email" required placeholder="Email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            <input type="password" required placeholder="Password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            <button disabled={loading} className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : null} Sign In
            </button>
          </form>
          {googleOn && (
            <>
              <div className="flex items-center gap-3 my-5"><div className="gold-line flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="gold-line flex-1" /></div>
              <button onClick={googleLogin} data-testid="google-login-btn" className="w-full py-3 text-sm rounded-full border border-white/20 text-foreground hover:bg-white/5 flex items-center justify-center gap-2">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continue with Google
              </button>
            </>
          )}
          <p className="text-sm text-muted-foreground text-center mt-6">New to The D? <Link to="/register" className="text-gold" data-testid="request-access-link">Request Access</Link></p>
          <p className="text-xs text-muted-foreground/70 text-center mt-2"><Link to="/forgot-password" className="hover:text-gold">Forgot password?</Link></p>
          <div className="text-center mt-6"><InstallApp variant="link" /></div>
        </div>
      </div>
    </div>
  );
}
